import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, Modal, Steps, Typography } from 'antd';
import { useAuth } from '@contexts/auth';
import { CommonUtility, PalletService, ToastMessage } from '@utility/index';
import {
  DEFAULT_LABEL_FORM_VALUES,
  InboundPalletLabelOptionsForm,
  type LabelFormValues,
} from './InboundPalletLabelOptionsForm';

const { Text } = Typography;

type ModalStep = 'create' | 'labels' | 'done';

type CreateFormValues = {
  count: number;
  prefix?: string;
};

type InboundCreateLicensePlatesModalProps = {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
  warehouseId: string;
  onCompleted?: () => void;
};

const STEP_ITEMS = [
  { title: 'Create plates' },
  { title: 'Generate labels' },
  { title: 'Done' },
];

function getPrefixPreview(prefix: string) {
  const trimmed = prefix.trim();
  if (!trimmed) return null;
  return `Examples: ${trimmed}XX, ${trimmed}XY`;
}

export function InboundCreateLicensePlatesModal({
  open,
  onClose,
  shipmentId,
  warehouseId,
  onCompleted,
}: InboundCreateLicensePlatesModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<ModalStep>('create');
  const [loading, setLoading] = useState(false);
  const [palletNumbers, setPalletNumbers] = useState<string[]>([]);
  const [createForm] = Form.useForm<CreateFormValues>();
  const [labelForm] = Form.useForm<LabelFormValues>();

  const prefix = Form.useWatch('prefix', createForm);

  const resetState = useCallback(() => {
    setStep('create');
    setPalletNumbers([]);
    createForm.resetFields();
    labelForm.resetFields();
    labelForm.setFieldsValue(DEFAULT_LABEL_FORM_VALUES);
  }, [createForm, labelForm]);

  useEffect(() => {
    if (step === 'labels') {
      labelForm.setFieldsValue(DEFAULT_LABEL_FORM_VALUES);
    }
  }, [step, labelForm]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const stepIndex = useMemo(() => {
    if (step === 'create') return 0;
    if (step === 'labels') return 1;
    return 2;
  }, [step]);

  const handleClose = () => {
    if (loading) return;
    onCompleted?.();
    onClose();
  };

  const handleCreatePlates = async (): Promise<boolean> => {
    try {
      const values = await createForm.validateFields();
      setLoading(true);

      const payload = CommonUtility.removeEmptyFields({
        warehouseId,
        shipmentId,
        count: values.count,
        prefix: values.prefix?.trim() || undefined,
      });

      const response = await PalletService.bulkCreatePallets(payload);
      const numbers = response?.data?.palletNumbers ?? [];
      const count = numbers.length || values.count;

      setPalletNumbers(numbers);
      ToastMessage.success(`${count} license plate${count === 1 ? '' : 's'} generated`);
      setStep('labels');
      return false;
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return false;
      }
      ToastMessage.apiError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLabels = async (): Promise<boolean> => {
    if (!palletNumbers.length) {
      ToastMessage.error('No license plates to generate labels for.');
      return false;
    }

    try {
      const values = await labelForm.validateFields();
      setLoading(true);

      await PalletService.generatePalletLabels({
        warehouseId,
        palletNumbers,
        labelSize: values.labelSize,
        pageSize: values.pageSize,
        labelFormat: values.labelFormat,
      });

      setStep('done');
      return false;
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return false;
      }
      ToastMessage.apiError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleOk = async (): Promise<void | boolean> => {
    if (step === 'done') {
      handleClose();
      return;
    }
    if (step === 'create') {
      return handleCreatePlates();
    }
    return handleGenerateLabels();
  };

  const modalTitle = useMemo(() => {
    if (step === 'create') return 'Create licence plates';
    if (step === 'labels') return 'Generate labels';
    return 'Labels requested';
  }, [step]);

  const prefixPreview = getPrefixPreview(prefix ?? '');

  return (
    <Modal
      title={modalTitle}
      open={open}
      centered
      onCancel={handleClose}
      destroyOnHidden
      confirmLoading={loading}
      okText={
        step === 'create' ? 'Create' : step === 'labels' ? 'Generate labels' : 'Close'
      }
      cancelText={step === 'done' ? undefined : 'Cancel'}
      cancelButtonProps={step === 'done' ? { style: { display: 'none' } } : undefined}
      onOk={handleOk}
    >
      <Steps
        current={stepIndex}
        items={STEP_ITEMS}
        size="small"
        style={{ marginBottom: 24 }}
      />

      {step === 'create' && (
        <Form form={createForm} layout="vertical" initialValues={{ count: 1 }}>
          <Form.Item
            name="count"
            label="Count"
            rules={[
              { required: true, message: 'Enter a count' },
              { type: 'number', min: 1, message: 'Count must be at least 1' },
            ]}
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="prefix" label="Prefix (optional)">
            <Input placeholder="e.g. BULK-" />
          </Form.Item>
          {prefixPreview ? (
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: -8 }}
            >
              {prefixPreview}
            </Text>
          ) : null}
        </Form>
      )}

      {step === 'labels' && <InboundPalletLabelOptionsForm form={labelForm} />}

      {step === 'done' && (
        <Text>
          Your labels are being prepared and will be sent to{' '}
          <Text strong>{user?.email ?? 'your email'}</Text> when ready.
        </Text>
      )}
    </Modal>
  );
}
